/** WCAG contrast helpers for the hero overlay colour pickers. */

const toRgb = (hex) => {
  const clean = String(hex || '#000000').replace('#', '');
  const full = clean.length === 3 ? clean.split('').map((c) => c + c).join('') : clean;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return [0, 0, 0];
  return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
};

const channel = (v) => {
  const s = v / 255;
  return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
};

export const luminance = (hex) => {
  const [r, g, b] = toRgb(hex);
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/** Composite an overlay colour at a given opacity over a mid-tone image estimate. */
export const compositeOverlay = (overlayHex, opacityPct, baseHex = '#808080') => {
  const a = Math.min(1, Math.max(0, (opacityPct ?? 100) / 100));
  const o = toRgb(overlayHex);
  const b = toRgb(baseHex);
  const mix = o.map((c, i) => Math.round(c * a + b[i] * (1 - a)));
  return `#${mix.map((c) => c.toString(16).padStart(2, '0')).join('')}`;
};

export const contrastRatio = (fgHex, bgHex) => {
  const l1 = luminance(fgHex);
  const l2 = luminance(bgHex);
  const [hi, lo] = l1 > l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
};

/**
 * Check heading (3:1 for large text) and body (4.5:1) against the effective
 * background the content sits on.
 */
export const checkHeroContrast = (hero) => {
  const effectiveBg = compositeOverlay(hero.overlay_color, hero.overlay_opacity);
  const headingRatio = contrastRatio(hero.heading_color, effectiveBg);
  const bodyRatio = contrastRatio(hero.body_color, effectiveBg);
  return {
    effectiveBg,
    headingRatio: Math.round(headingRatio * 100) / 100,
    bodyRatio: Math.round(bodyRatio * 100) / 100,
    headingPasses: headingRatio >= 3,
    bodyPasses: bodyRatio >= 4.5,
    passes: headingRatio >= 3 && bodyRatio >= 4.5,
  };
};

export const checkButtonContrast = (bg, fg) => {
  const ratio = contrastRatio(fg, bg);
  return { ratio: Math.round(ratio * 100) / 100, passes: ratio >= 4.5 };
};
